import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Card, Dropdown, Menu, message, Result, Space, Table, Tabs } from "antd";
import { DownOutlined, FileExcelOutlined, FilePdfOutlined } from "@ant-design/icons";
import { useDispatch } from "react-redux";
import {
  downloadReportExport,
  expenseApi,
  formatMoney,
  isAccountTeam,
  isAdminOrHR,
  isSubAdmin,
} from "./expenseHelpers";
import { hideAuthLoader, showAuthLoader } from "../../appRedux/actions/Auth";
import { getResource } from "../../util/getResourcePermission";
import { YearSelect } from "../../util/YearOptions";
import config from "../../permission/config.json";
import Service from "../../service";
import NodataFoundIcon from "../../assets/images/NodataFoundIcon";
import EmpNameCodeCell from "../common/EmpNameCodeCell";
import { Select } from "antd";
import "./expenseManagement.css";
import { PAGE_SIZE_OPTIONS } from "../../constants/tablePagination";
import { QuestionCircleOutlined } from "@ant-design/icons";
import GuidedTour, { hasTourFlag } from "../common/GuidedTour/GuidedTour";

const { TabPane } = Tabs;

const TOUR_STORAGE_KEY = "tour_seen_expense_reports";
const TOUR_HINT_STORAGE_KEY = "tour_hint_dismissed_expense_reports";

// Money-valued columns across the 9 reports (server/controller/expenseReport.js
// column definitions) - matched by dataIndex, not by name pattern, since a few
// non-money fields (utilization, overBudget) also contain "amount"-adjacent
// words and are pre-formatted strings server-side (e.g. "42.0%") that must NOT
// be run through formatMoney.
const CURRENCY_COLUMN_KEYS = new Set([
  "advanceAmount",
  "consumed",
  "remaining",
  "reimbursementRequired",
  "amountToReturn",
  "totalAmount",
  "amount",
  "budget",
  "actual",
  "variance",
]);

// Columns holding a server-formatted "Full Name (EMP_CODE)" string - rendered
// with the shared EmpNameCodeCell component (stacked name/code) instead of
// the raw string, matching the employee-name layout used everywhere else.
const EMP_NAME_COLUMN_KEYS = new Set(["employeeName", "actionBy"]);
const EMP_NAME_PATTERN = /^(.*)\s\(([^)]+)\)$/;

const renderEmpNameCell = (value) => {
  const match = typeof value === "string" ? value.match(EMP_NAME_PATTERN) : null;
  if (!match) return value;
  return <EmpNameCodeCell name={match[1]} code={match[2]} />;
};

// The 9 reports. `financeOnly` reports are visible only to Account Team / HR / Admin
// (section 8 role gating); the rest are available to any user who can reach the page.
const REPORTS = [
  { key: "employee-expense", label: "Employee Expense", financeOnly: false },
  { key: "advance-outstanding", label: "Advance Outstanding", financeOnly: true },
  { key: "expense-category", label: "Expense Category", financeOnly: false },
  { key: "department-wise", label: "Department-wise", financeOnly: true },
  { key: "grade-wise", label: "Grade-wise", financeOnly: true },
  { key: "monthly-summary", label: "Monthly Summary", financeOnly: false },
  { key: "budget-vs-actual", label: "Budget vs Actual", financeOnly: true },
  { key: "pending-settlement", label: "Pending Settlement", financeOnly: true },
  { key: "audit-trail", label: "Audit Trail", financeOnly: true },
];

const ExpenseReports = () => {
  const dispatch = useDispatch();
  const canRead = useMemo(
    () => getResource(config.RESOURCES.EXPENSE_REPORTS, ["read"]),
    []
  );
  const isFinance = useMemo(() => isAccountTeam() || isAdminOrHR() || isSubAdmin(), []);
  const visibleReports = useMemo(
    () => REPORTS.filter((r) => !r.financeOnly || isFinance),
    [isFinance]
  );

  const [activeKey, setActiveKey] = useState(visibleReports[0]?.key);
  const [report, setReport] = useState(null);
  // Server-side pagination - the on-screen page is fetched from the backend; the
  // export always downloads the full dataset regardless of the current page.
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 25,
    total: 0,
  });
  // Which export ("excel" | "pdf") is currently generating, so we can show a
  // loader + message until the (potentially slow) backend call finishes.
  const [exportingFormat, setExportingFormat] = useState(null);
  // Monthly Summary-only filter - backend defaults to the current year when
  // omitted (server/controller/expenseReport.js monthlyExpenseSummary), so
  // this mirrors that default instead of hardcoding it separately.
  const [summaryYear, setSummaryYear] = useState(String(new Date().getFullYear()));
  const isMonthlySummary = activeKey === "monthly-summary";

  const [isTourOpen, setIsTourOpen] = useState(false);
  const [isTourHintOpen, setIsTourHintOpen] = useState(false);

  useEffect(() => {
    if (!hasTourFlag(TOUR_STORAGE_KEY) && !hasTourFlag(TOUR_HINT_STORAGE_KEY)) {
      setIsTourHintOpen(true);
    }
  }, []);

  const tourSteps = useMemo(
    () => [
      {
        target: '[data-tour="er-tabs"]',
        title: "Reports",
        content: isFinance
          ? "Switch between the available reports here. Some, like Advance Outstanding and Audit Trail, are only visible to Finance/HR/Admin."
          : "Switch between the reports available to you here.",
      },
      {
        target: '[data-tour="er-export"]',
        title: "Export",
        content: "Download the full report (not just the current page) as Excel or PDF.",
      },
      {
        target: '[data-tour="er-table"]',
        title: "Report Data",
        content: "The report table for the selected tab, with server-side pagination.",
      },
    ],
    [isFinance]
  );

  const hintSteps = useMemo(
    () => [
      {
        target: '[data-tour="er-guide-icon"]',
        title: "New here?",
        content: "Click here for a quick guide to this screen.",
        shape: "circle",
      },
    ],
    []
  );

  const loadReport = useCallback(
    async (key) => {
      if (!key) return;
      dispatch(showAuthLoader());
      try {
        const params = {
          page: pagination.current,
          limit: pagination.pageSize,
        };
        if (key === "monthly-summary") params.year = Number(summaryYear);
        const data = await expenseApi.get(`${Service.expenseReport}/${key}`, params);
        setReport(data);
        setPagination((prev) => ({ ...prev, total: data?.total || 0 }));
      } catch (err) {
        message.error(err.message || "Failed to load report");
        setReport(null);
      } finally {
        dispatch(hideAuthLoader());
      }
    },
    [dispatch, pagination.current, pagination.pageSize, summaryYear]
  );

  useEffect(() => {
    if (canRead) loadReport(activeKey);
  }, [canRead, activeKey, loadReport]);

  // Ant passes the next pagination object (current / pageSize) here; updating it
  // re-triggers loadReport via the useCallback deps.
  const handleTableChange = (nextPagination) => {
    setPagination((prev) => ({ ...prev, ...nextPagination }));
  };

  // Export the active report; shows a loader on the button + a persistent hint
  // message until the backend finishes generating the file.
  const handleExport = async (format) => {
    if (exportingFormat) return;
    setExportingFormat(format);
    const hide = message.loading(
      `Generating ${format === "excel" ? "Excel" : "PDF"} export… this may take a moment.`,
      0
    );
    try {
      await downloadReportExport(
        activeKey,
        format,
        isMonthlySummary ? { year: Number(summaryYear) } : {}
      );
    } finally {
      hide();
      setExportingFormat(null);
    }
  };

  // Build Ant columns from the report's `columns` metadata (title/dataIndex pairs
  // or plain strings), falling back to keys inferred from the first row.
  //
  // Date fields (dateIssued, expectedSettlementDate, etc.) are formatted
  // server-side by expenseReport.js's shared formatDate() using the
  // company's own date format (server/controller/expenseReport.js), so the
  // same formatted string is correct for both the on-screen JSON view and
  // Excel/PDF export - no client-side reformatting needed here.
  const columns = useMemo(() => {
    if (!report) return [];
    const withFallback = (col) => ({
      ...col,
      render: (value) => {
        if (value === null || value === undefined || value === "") return "-";
        if (CURRENCY_COLUMN_KEYS.has(col.key) && typeof value === "number") {
          return formatMoney(value);
        }
        if (EMP_NAME_COLUMN_KEYS.has(col.key)) {
          return renderEmpNameCell(value);
        }
        return value;
      },
    });
    if (Array.isArray(report.columns) && report.columns.length) {
      return report.columns
        .map((col) =>
          typeof col === "string"
            ? { title: col, dataIndex: col, key: col }
            : {
                title: col.header || col.title || col.label || col.dataIndex || col.key,
                dataIndex: col.dataIndex || col.key,
                key: col.dataIndex || col.key,
              }
        )
        .filter((col) => !(activeKey === "audit-trail" && col.key === "at"))
        .map(withFallback);
    }
    const first = (report.rows || [])[0] || {};
    return Object.keys(first)
      .filter((k) => !(activeKey === "audit-trail" && k === "at"))
      .map((k) => withFallback({ title: k, dataIndex: k, key: k }));
  }, [report, activeKey]);

  if (!canRead) {
    return (
      <Card className="employee-card">
        <Result
          status="403"
          title="Access Denied"
          subTitle="You do not have permission to view expense reports."
        />
      </Card>
    );
  }

  return (
    <Card className="employee-card expense-page">
      <div className="inout-header text-left">
        <h2>
          Expense Reports{" "}
          <QuestionCircleOutlined
            data-tour="er-guide-icon"
            className="guide-title-icon"
            role="button"
            tabIndex={0}
            onClick={() => setIsTourOpen(true)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") setIsTourOpen(true);
            }}
            title="Click for a step-by-step guide"
          />
        </h2>
      </div>
      <Card className="main-content-wrapper">
        <div className="search-wrapper">
          <div className="filter-btns-wrapper" data-tour="er-export">
            {isMonthlySummary && (
              <Select
                value={summaryYear}
                onChange={(year) => {
                  setSummaryYear(year);
                  setPagination((prev) => ({ ...prev, current: 1 }));
                }}
                options={YearSelect()}
                style={{ width: 120, marginRight: 8 }}
              />
            )}
            <Dropdown
              trigger={["click"]}
              disabled={!!exportingFormat}
              overlay={
                <Menu onClick={({ key }) => handleExport(key)}>
                  <Menu.Item key="excel" icon={<FileExcelOutlined />}>
                    Export Excel
                  </Menu.Item>
                  <Menu.Item key="pdf" icon={<FilePdfOutlined />}>
                    Export PDF
                  </Menu.Item>
                </Menu>
              }
            >
              <Button className="btn-secondary" loading={!!exportingFormat}>
                <Space size={6}>
                  Export
                  <DownOutlined />
                </Space>
              </Button>
            </Dropdown>
          </div>
        </div>

        <Tabs
          activeKey={activeKey}
          onChange={(key) => {
            setActiveKey(key);
            // Reset to the first page when switching reports.
            setPagination((prev) => ({ ...prev, current: 1 }));
          }}
          className="expense-report-tabs"
          data-tour="er-tabs"
        >
          {visibleReports.map((r) => (
            <TabPane tab={r.label} key={r.key} />
          ))}
        </Tabs>

        {report && (
          <div className="main-table-wrapper" data-tour="er-table">
            <Table
              className="expense-reports-table"
              rowKey={(_, idx) => idx}
              columns={columns}
              dataSource={report.rows || []}
              locale={{ emptyText: <NodataFoundIcon /> }}
              pagination={{
                showSizeChanger: true,
                pageSizeOptions: PAGE_SIZE_OPTIONS,
                showTotal: (t, range) => `${range[0]}-${range[1]} of ${t}`,
                ...pagination,
              }}
              onChange={handleTableChange}
              scroll={{ x: "max-content" }}
            />
          </div>
        )}
      </Card>

      {isTourOpen && (
        <GuidedTour steps={tourSteps} onClose={() => setIsTourOpen(false)} storageKey={TOUR_STORAGE_KEY} />
      )}
      {!isTourOpen && isTourHintOpen && (
        <GuidedTour steps={hintSteps} onClose={() => setIsTourHintOpen(false)} storageKey={TOUR_HINT_STORAGE_KEY} />
      )}
    </Card>
  );
};

export default ExpenseReports;
